// =============================================================================
// Portal Payments Page — Wholesale invoice rollup + per-order list
// -----------------------------------------------------------------------------
// Read-only: shows the merchant the wholesale amount Transfer Kingdom debits
// from their prepaid wallet, along with the list of orders that make up
// that total. Does NOT move money — top-ups happen on the Wallet page,
// payment methods on the Payment methods page.
// =============================================================================

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  Card,
  CardHeader,
  Select,
  Badge,
  Button,
  Spinner,
} from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';
import { StoreFilter, NO_STORE } from '../components/StoreFilter';
import { useConnections } from '../hooks/use-connections';
import {
  ORDER_STATUS_MERCHANT_VARIANT,
  getMerchantOrderStatusLabel,
} from '@tkpod/shared';

const SETTLEMENT_OPTIONS = [
  { value: 'UNSETTLED', label: 'Unsettled' },
  { value: 'ALL', label: 'All orders' },
];

const SORT_OPTIONS = [
  { value: 'desc', label: 'Newest First' },
  { value: 'asc', label: 'Oldest First' },
];

interface PaymentsSummary {
  outstandingTotal: string;
  outstandingOrderCount: number;
  monthToDateTotal: string;
  monthToDateOrderCount: number;
  currency: string;
  periodStart: string;
  periodEnd: string;
}

interface InvoiceableOrder {
  id: string;
  orderNumber: string;
  status: string;
  source: string;
  createdAt: string;
  customerName: string | null;
  wholesaleProductionCost: string | null;
  wholesaleShippingCost: string | null;
  wholesaleTotalCost: string | null;
  currency: string;
  store: { id: string; name: string; shopDomain: string | null } | null;
}

interface OrdersResponse {
  data: InvoiceableOrder[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

function formatMoney(value: string | null, currency: string): string {
  if (value == null) return '—';
  const num = Number.parseFloat(value);
  if (!Number.isFinite(num)) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency}`;
  }
}

export function PortalPaymentsPage() {
  const api = useApiClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // NaN-safe page parse: URLSearchParams preserves user-typed garbage like
  // `?page=abc`. Without this clamp we would push "NaN" back into the URL
  // and the API would reject the pagination DTO with a validation error.
  const rawPage = Number(searchParams.get('page') ?? '1');
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? Math.floor(rawPage) : 1;
  const settlement =
    (searchParams.get('settlement') as 'UNSETTLED' | 'ALL' | null) ??
    'UNSETTLED';
  const sortOrder = searchParams.get('sortOrder') ?? 'desc';
  const connectionId = searchParams.get('connectionId') ?? '';

  const setFilter = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.set('page', '1');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const connectionsQuery = useConnections();

  const summaryQuery = useQuery({
    queryKey: ['portal', 'payments', 'summary', { connectionId }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (connectionId) params.set('connectionId', connectionId);
      const qs = params.toString();
      return api.get<PaymentsSummary>(
        `/api/v1/payments/summary${qs ? `?${qs}` : ''}`,
      );
    },
    refetchInterval: 60_000,
  });

  const ordersQuery = useQuery({
    queryKey: [
      'portal',
      'payments',
      'orders',
      { page, settlement, sortOrder, connectionId },
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      params.set('settlement', settlement);
      params.set('sortOrder', sortOrder);
      if (connectionId) params.set('connectionId', connectionId);
      return api.get<OrdersResponse>(
        `/api/v1/payments/orders?${params.toString()}`,
      );
    },
  });

  // Unwrap the potential `{ data }` envelope without losing the inner data
  // shape. In practice the API returns the summary body directly, but we
  // stay defensive if a future interceptor starts wrapping responses.
  const summaryRaw = summaryQuery.data as unknown as
    | ({ data?: PaymentsSummary } & PaymentsSummary)
    | undefined;
  const summary: PaymentsSummary | undefined = summaryRaw?.data ?? summaryRaw;

  const orders = ordersQuery.data?.data ?? [];
  const pagination = ordersQuery.data?.pagination;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardHeader title="Outstanding wholesale balance" />
          <div className="px-4 pb-4 sm:px-5 sm:pb-5">
            {summaryQuery.isLoading ? (
              <Spinner size="sm" />
            ) : summary ? (
              <>
                <p className="text-2xl font-bold text-text-primary">
                  {formatMoney(summary.outstandingTotal, summary.currency)}
                </p>
                <p className="mt-1 text-xs text-text-secondary">
                  Across {summary.outstandingOrderCount} order
                  {summary.outstandingOrderCount !== 1 ? 's' : ''}
                </p>
                <p className="mt-2 text-[11px] text-text-tertiary">
                  Debited from your prepaid wallet when each order is
                  received from your storefront.
                </p>
              </>
            ) : (
              <p className="text-sm text-text-tertiary">Summary unavailable.</p>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Month to date" />
          <div className="px-4 pb-4 sm:px-5 sm:pb-5">
            {summaryQuery.isLoading ? (
              <Spinner size="sm" />
            ) : summary ? (
              <>
                <p className="text-2xl font-bold text-text-primary">
                  {formatMoney(summary.monthToDateTotal, summary.currency)}
                </p>
                <p className="mt-1 text-xs text-text-secondary">
                  {summary.monthToDateOrderCount} order
                  {summary.monthToDateOrderCount !== 1 ? 's' : ''} this period
                </p>
                <p className="mt-2 text-[11px] text-text-tertiary">
                  {new Date(summary.periodStart).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </>
            ) : (
              <p className="text-sm text-text-tertiary">Summary unavailable.</p>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="How we bill" />
          <div className="space-y-2 px-4 pb-4 sm:px-5 sm:pb-5 text-sm">
            <p className="text-text-primary">
              Transfer Kingdom bills only the wholesale production + shipping
              cost of received POD orders. Charges are debited from your
              wallet immediately when the order is received; production
              starts once the charge clears. Top up the wallet (or enable
              auto-recharge) to keep fulfillment uninterrupted.
            </p>
            <Link
              to="/billing?tab=wallet"
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
            >
              Manage wallet
              <svg
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </Link>
          </div>
        </Card>
      </div>

      {/* Orders list */}
      <Card padding="none">
        <div className="flex flex-col gap-4 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              Invoiceable orders
            </h2>
            <p className="mt-0.5 text-xs text-text-tertiary">
              {pagination != null
                ? `${pagination.total} order${pagination.total !== 1 ? 's' : ''}`
                : 'Orders contributing to your wholesale invoice'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-[180px]">
              <StoreFilter
                value={connectionId}
                onChange={(v) => setFilter('connectionId', v)}
                includeManual
              />
            </div>
            <div className="w-[160px]">
              <Select
                value={settlement}
                onChange={(e) => setFilter('settlement', e.target.value)}
                options={SETTLEMENT_OPTIONS}
                aria-label="Filter by settlement"
              />
            </div>
            <div className="w-[160px]">
              <Select
                value={sortOrder}
                onChange={(e) => setFilter('sortOrder', e.target.value)}
                options={SORT_OPTIONS}
                aria-label="Sort orders"
              />
            </div>
          </div>
        </div>

        {/* Shared-balance reminder when a single store is selected. Billing
            (wallet + payment methods) lives at the organization level — the
            store filter only narrows which orders are shown, not how money
            moves. This keeps merchants from assuming each store has its own
            balance. */}
        {connectionId && (
          <div className="border-b border-border bg-surface-secondary/30 px-5 py-2 text-xs text-text-tertiary">
            Showing wholesale figures for{' '}
            <span className="font-medium text-text-secondary">
              {connectionId === NO_STORE
                ? 'manual (no store) orders'
                : (connectionsQuery.data?.find((c) => c.id === connectionId)
                    ?.name ?? 'the selected store')}
            </span>
            . Your wallet balance and payment methods are shared across all
            stores in this account.
          </div>
        )}

        {ordersQuery.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : ordersQuery.isError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <p className="text-sm text-text-secondary">
              Failed to load payments data.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => ordersQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : orders.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-sm text-text-secondary">
              No invoiceable orders yet.
            </p>
            <p className="mt-1 text-xs text-text-tertiary">
              Once you fulfill your first POD order, the wholesale cost will
              appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-secondary/40">
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Order
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Customer
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary md:table-cell">
                    Status
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary lg:table-cell">
                    Store
                  </th>
                  <th className="hidden px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary lg:table-cell">
                    Production
                  </th>
                  <th className="hidden px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary lg:table-cell">
                    Shipping
                  </th>
                  <th className="px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    TK total
                  </th>
                  <th
                    className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary lg:table-cell"
                  >
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {orders.map((o) => (
                  <tr
                    key={o.id}
                    className="cursor-pointer transition-colors hover:bg-surface-secondary/60"
                    onClick={() => navigate(`/orders/${o.id}`)}
                  >
                    <td className="px-5 py-2.5 font-medium text-brand-600 whitespace-nowrap">
                      {o.orderNumber}
                    </td>
                    <td className="px-5 py-2.5 truncate max-w-[200px] text-text-primary">
                      {o.customerName ?? '—'}
                    </td>
                    <td className="hidden px-5 py-2.5 whitespace-nowrap md:table-cell">
                      <Badge
                        variant={ORDER_STATUS_MERCHANT_VARIANT[o.status] ?? 'default'}
                        size="sm"
                      >
                        {getMerchantOrderStatusLabel(o.status)}
                      </Badge>
                    </td>
                    <td className="hidden px-5 py-2.5 truncate max-w-[160px] text-text-secondary lg:table-cell">
                      {o.store?.name ?? (
                        <span className="text-text-tertiary">Manual</span>
                      )}
                    </td>
                    <td className="hidden px-5 py-2.5 text-right whitespace-nowrap text-text-secondary lg:table-cell">
                      {formatMoney(o.wholesaleProductionCost, o.currency)}
                    </td>
                    <td className="hidden px-5 py-2.5 text-right whitespace-nowrap text-text-secondary lg:table-cell">
                      {formatMoney(o.wholesaleShippingCost, o.currency)}
                    </td>
                    <td className="px-5 py-2.5 text-right whitespace-nowrap font-semibold text-text-primary">
                      {formatMoney(o.wholesaleTotalCost, o.currency)}
                    </td>
                    <td className="hidden px-5 py-2.5 whitespace-nowrap text-[12px] text-text-tertiary lg:table-cell">
                      {new Date(o.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination != null && pagination.totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
            <span className="text-xs text-text-tertiary">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <div className="flex gap-1.5">
              <button
                type="button"
                className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-surface-secondary disabled:pointer-events-none disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => setFilter('page', String(page - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-surface-secondary disabled:pointer-events-none disabled:opacity-50"
                disabled={page >= pagination.totalPages}
                onClick={() => setFilter('page', String(page + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Inline FAQ */}
      <Card>
        <CardHeader title="Frequently asked" />
        <div className="space-y-3 px-4 pb-4 sm:px-5 sm:pb-5 text-sm">
          <details className="group">
            <summary className="cursor-pointer font-medium text-text-primary">
              Why does TK invoice me an amount different from what the
              customer paid on Shopify?
            </summary>
            <p className="mt-2 text-text-secondary">
              Transfer Kingdom is your POD supplier — we bill you the
              wholesale production and shipping cost of fulfilling the order.
              What your customer paid on Shopify (product price, retail
              shipping, taxes) is paid to you and is independent of the
              wholesale cost we bill.
            </p>
          </details>
          <details className="group">
            <summary className="cursor-pointer font-medium text-text-primary">
              When am I charged?
            </summary>
            <p className="mt-2 text-text-secondary">
              Wallet debits happen the moment we receive an order from
              your Shopify storefront (`orders/create` webhook). The
              wholesale cost (production + shipping) is taken from your
              wallet immediately so production can start without delay.
              Auto-recharge keeps the wallet topped up so accepting new
              orders is never blocked by a temporary balance gap.
            </p>
          </details>
          <details className="group">
            <summary className="cursor-pointer font-medium text-text-primary">
              How do I pay?
            </summary>
            <p className="mt-2 text-text-secondary">
              Top up your Transfer Kingdom wallet from the{' '}
              <Link
                to="/billing?tab=wallet"
                className="font-medium text-brand-600 hover:underline dark:text-brand-400"
              >
                Wallet page
              </Link>{' '}
              using any saved card. You can enable auto-recharge to refill
              automatically when the balance falls below your chosen
              threshold.
            </p>
          </details>
        </div>
      </Card>
    </div>
  );
}
